import React from "react";
import { blogInputClass } from "./BlogEditorFormFields";

const fieldLabelClass = "mb-1.5 block text-xs font-medium text-slate-600";

export function createEmptyTableContent({
  caption = "Specifications",
  headers = ["Tests", "Method", "Grade 1", "Grade 2"],
  rows = 3
} = {}) {
  const cols = headers.length;
  return {
    caption,
    headers,
    rows: new Array(rows).fill(null).map(() => new Array(cols).fill(""))
  };
}

export function normalizeTableContent(content) {
  const headers =
    Array.isArray(content?.headers) && content.headers.length > 0
      ? content.headers.map((h) => String(h ?? ""))
      : ["Column 1", "Column 2"];
  const colCount = headers.length;
  const sourceRows = Array.isArray(content?.rows) ? content.rows : [];
  const rows = (sourceRows.length > 0 ? sourceRows : [[]]).map((row) => {
    const cells = Array.isArray(row) ? row.map((cell) => String(cell ?? "")) : [];
    while (cells.length < colCount) cells.push("");
    return cells.slice(0, colCount);
  });

  return {
    caption: content?.caption || "",
    headers,
    rows
  };
}

/**
 * Editable header + body grid for product spec tables.
 * `value` shape: { caption, headers: string[], rows: string[][] }
 */
export function TableGridEditor({ value, onChange, captionPlaceholder = "Specifications" }) {
  const table = normalizeTableContent(value);

  const commit = (partial) => onChange(normalizeTableContent({ ...table, ...partial }));

  const setHeader = (colIndex, nextValue) => {
    const headers = table.headers.map((h, i) => (i === colIndex ? nextValue : h));
    commit({ headers });
  };

  const setCell = (rowIndex, colIndex, nextValue) => {
    const rows = table.rows.map((row, r) =>
      r === rowIndex ? row.map((cell, c) => (c === colIndex ? nextValue : cell)) : row
    );
    commit({ rows });
  };

  const addColumn = () => {
    const headers = [...table.headers, `Column ${table.headers.length + 1}`];
    const rows = table.rows.map((row) => [...row, ""]);
    commit({ headers, rows });
  };

  const removeColumn = (colIndex) => {
    if (table.headers.length <= 1) return;
    const headers = table.headers.filter((_, i) => i !== colIndex);
    const rows = table.rows.map((row) => row.filter((_, i) => i !== colIndex));
    commit({ headers, rows });
  };

  const addRow = () => {
    commit({ rows: [...table.rows, new Array(table.headers.length).fill("")] });
  };

  const removeRow = (rowIndex) => {
    if (table.rows.length <= 1) return;
    commit({ rows: table.rows.filter((_, i) => i !== rowIndex) });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className={fieldLabelClass}>Table title</label>
        <input
          className={blogInputClass}
          placeholder={captionPlaceholder}
          value={table.caption}
          onChange={(e) => commit({ caption: e.target.value })}
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-slate-50">
              {table.headers.map((header, colIndex) => (
                <th key={`h-${colIndex}`} className="min-w-[120px] border-b border-slate-200 p-1.5 align-top">
                  <input
                    className="w-full rounded border border-slate-200 px-2 py-1 text-xs font-semibold"
                    value={header}
                    onChange={(e) => setHeader(colIndex, e.target.value)}
                    placeholder={`Header ${colIndex + 1}`}
                  />
                  <button
                    type="button"
                    className="mt-1 text-[10px] font-medium text-red-600 disabled:opacity-40"
                    disabled={table.headers.length <= 1}
                    onClick={() => removeColumn(colIndex)}
                  >
                    Remove column
                  </button>
                </th>
              ))}
              <th className="w-10 border-b border-slate-200 p-1.5" />
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={`r-${rowIndex}`}>
                {row.map((cell, colIndex) => (
                  <td key={`c-${rowIndex}-${colIndex}`} className="border-b border-slate-100 p-1.5">
                    <input
                      className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
                      value={cell}
                      onChange={(e) => setCell(rowIndex, colIndex, e.target.value)}
                    />
                  </td>
                ))}
                <td className="border-b border-slate-100 p-1.5">
                  <button
                    type="button"
                    className="text-[10px] font-medium text-red-600 disabled:opacity-40"
                    disabled={table.rows.length <= 1}
                    onClick={() => removeRow(rowIndex)}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-white"
          onClick={addRow}
        >
          + Add row
        </button>
        <button
          type="button"
          className="rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-white"
          onClick={addColumn}
        >
          + Add column
        </button>
      </div>
    </div>
  );
}
